import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Form, Button, Typography, Row, Col, Popover } from "antd";
import FormActions from "../../../components/forms/FormActions";
import { FormattedMessage } from "react-intl";
import { InfoCircleOutlined } from "@ant-design/icons";
import OverlappingShifts from "./OverlappingShifts";
import {
  InputItem,
  SelectItem as SelectItemComponent,
  HourPickerItem,
} from "../../../components/forms/FormItems";
import { RestrictedSettingsDataSelect } from "../../../components/forms/AutoCompleteFormItem";
import ChildrenAnimationWrapper from "../../../components/animations/ChildrenAnimationWrapper";
import AnimationChildWrapper from "../../../components/animations/AnimationChildWrapper";
import { CloseOutlined } from "@ant-design/icons";
import Moment from "moment";
import { extendMoment } from "moment-range";
const moment = extendMoment(Moment);
const { Title, Text } = Typography;
var unflatten = require("flat").unflatten;

const Shift = (props) => {
  const {
    resource,
    action,
    name,
    onFinish,
    onDecline,
    mode,
    onFinishFailed,
    activeItem,
    value,
    selectedVersion,
    clientTariffId,
  } = props;
  const [form] = Form.useForm();
  const [disabled, setDisabled] = useState(true);
  const [overlappingShifts, setOverlappingShifts] = useState([]);
  const [dayOfWeek, setDayOfWeek] = useState();

  useEffect(() => {
    setDayOfWeek(activeItem && activeItem["day_type"]);
    if (mode === "edit" && activeItem) {
      var keys = Object.keys(activeItem);
      let item = {};
      keys.forEach((key, i) => {
        item[name + "." + key] = activeItem[key];
      });
      form.setFieldsValue(item);
      setDisabled(false);
    } else {
      form.setFieldsValue({
        "shifts.hourly_percentage_rate": 100,
        "shifts.client_tariff_id": clientTariffId,
      });
      setDisabled(true);
    }
  }, [activeItem, mode]);

  const onSubmit = (values) => {
    if (mode === "edit") {
      values[name + ".id"] = activeItem.id;
    }
    setOverlappingShifts([]);
    checkIfOverlapping(values, mode);
  };

  const checkIfOverlapping = (values, mode) => {
    const filtered =
      value && mode === "edit"
        ? value.filter((i) => i.id !== activeItem.id)
        : value;
    let overLapping = [];
    if (filtered && filtered.length > 0) {
      const newShift = unflatten(values, { safe: true })["shifts"];
      if (newShift.day_of_week && newShift.day_type === 1) {
        const dayOfWeekOverlapping = getDayOfWeekOverlapping(
          newShift,
          filtered
        );
        overLapping = dayOfWeekOverlapping;
      }
      if (newShift.day_type !== 1) {
        const holidayOverlapping = getHolidayOverlapping(newShift, filtered);
        if (holidayOverlapping) {
          overLapping = holidayOverlapping;
        }
      }
    }
    setOverlappingShifts(overLapping);
    if (overLapping.length === 0) {
      onFinish(values, mode);
      form.resetFields();
    }
  };

  const getDayOfWeekOverlapping = (newShift, shifts) => {
    let overLapping = [];
    newShift.day_of_week.forEach((newShiftDay, i) => {
      const start = getdateInCurrentWeek(
        newShift.start_time_minutes,
        newShiftDay
      );
      const end = start.clone().add(newShift.end_time_minutes, "hours");
      const range1 = moment.range(start, end);
      const filtered = shifts.filter((i) => i.day_type === newShift.day_type);
      filtered.forEach((oldShift, i) => {
        if (oldShift.day_of_week.length > 0) {
          oldShift.day_of_week.forEach((oldShiftDay, j) => {
            const oldStart = getdateInCurrentWeek(
              oldShift.start_time_minutes,
              oldShiftDay
            );
            const oldEnd = oldStart
              .clone()
              .add(oldShift.end_time_minutes, "hours");
            const range2 = moment.range(oldStart, oldEnd);
            let satuardayCoflict = false;

            if (newShiftDay === 6) {
              //on satuardays we should check conflicts with sundays
              const weekBeforeStart = getdateInCurrentWeek(
                newShift.start_time_minutes,
                newShiftDay
              ).subtract(7, "days");
              const weekBeforeEnd = weekBeforeStart
                .clone()
                .add(newShift.end_time_minutes, "hours");
              const range3 = moment.range(weekBeforeStart, weekBeforeEnd);
              if (range3.overlaps(range2, { adjacent: false })) {
                satuardayCoflict = true;
              }
            }

            if (
              range1.overlaps(range2, { adjacent: false }) ||
              satuardayCoflict
            ) {
              overLapping.push({
                id: oldShift.id,
                day: oldShiftDay,
                day_type: oldShift.day_type,
                start: oldShift.start_time_minutes,
                duration: oldShift.end_time_minutes,
              });
            }
          });
        }
      });
    });
    return overLapping;
  };

  const getHolidayOverlapping = (newShift, shifts) => {
    let overLapping = [];
    const start = getdateInCurrentWeek(newShift.start_time_minutes, 2);
    const end = start.clone().add(newShift.end_time_minutes, "hours");
    const range1 = moment.range(start, end);
    const filtered = shifts.filter((i) => i.day_type === newShift.day_type);
    filtered.forEach((oldShift, i) => {
      const oldStart = getdateInCurrentWeek(oldShift.start_time_minutes, 2);
      const oldEnd = oldStart.clone().add(oldShift.end_time_minutes, "hours");
      const range2 = moment.range(oldStart, oldEnd);

      const oldStartDayBefore = getdateInCurrentWeek(
        oldShift.start_time_minutes,
        1
      );
      const oldEndDayBefore = oldStartDayBefore
        .clone()
        .add(oldShift.end_time_minutes, "hours");
      const range3 = moment.range(oldStartDayBefore, oldEndDayBefore);

      if (
        range1.overlaps(range2, { adjacent: false }) ||
        range1.overlaps(range3, { adjacent: false })
      ) {
        overLapping.push({
          id: oldShift.id,
          // day: oldShiftDay,
          day_type: oldShift.day_type,
          start: oldShift.start_time_minutes,
          duration: oldShift.end_time_minutes,
        });
      }
    });
    return overLapping;
  };

  const getdateInCurrentWeek = (date, day) => {
    const start = moment(date);
    var mmtMidnight = start.clone().startOf("day");
    var diffMinutes = start.diff(mmtMidnight, "minutes") + day * 1440;
    return moment().startOf("week").add(diffMinutes, "minutes");
  };

  const onCancel = () => {
    form.resetFields();
    onDecline();
  };

  const onValuesChange = (values, all) => {
    let result = false;
    const required = [
      "shifts.day_type",
      "shifts.start_time_minutes",
      "shifts.end_time_minutes",
      "shifts.salary_symbol_id",
    ];
    if (dayOfWeek === 1) {
      required.push("shifts.day_of_week");
    }
    required.forEach((item, i) => {
      if (
        all[item] === undefined ||
        all[item] === "" ||
        all[item] === null ||
        all[item].length === 0
      ) {
        result = true;
      }
    });
    setDisabled(result);
  };

  const layout = {
    labelCol: { span: 9 },
    wrapperCol: { span: 15 },
  };

  const content = <OverlappingShifts overlappingShifts={overlappingShifts} />;
  const onDayTypeChange = (val) => {
    setDayOfWeek(val);
  };

  return (
    <div>
      <div className="header" style={{ height: "40px" }}>
        <Button
          style={{ position: "absolute", left: 10, top: 10 }}
          className="closeBtn"
          type="text"
          shape="circle"
          icon={<CloseOutlined />}
          onClick={onCancel}
        />
      </div>
      <Title
        style={{
          fontSize: "30px",
          fontWeight: 500,
          lineHeight: 1.27,
          color: "rgba(0, 0, 0, 0.85)",
          margin: "13px 0 40px 0 ",
          textAlign: "center",
        }}
      >
        {<FormattedMessage id="app.contracts.addShift" />}
      </Title>
      <Form
        {...layout}
        onFinish={onSubmit}
        onFinishFailed={onFinishFailed}
        form={form}
        style={{
          padding: "0 60px 0px 60px",
          width: "600px",
        }}
        onValuesChange={onValuesChange}
      >
        <ChildrenAnimationWrapper>
          <Row>
            <Col key={1} span={24} style={{ padding: "0 20px" }}>
              <AnimationChildWrapper key={1} itemKey={1}>
                <SelectItemComponent
                  name={"shifts.day_type"}
                  category={"contract"}
                  required={true}
                  contractDayType={true}
                  onChange={onDayTypeChange}
                />
              </AnimationChildWrapper>
              {dayOfWeek === 1 && (
                <AnimationChildWrapper key={2} itemKey={2}>
                  <SelectItemComponent
                    name={"shifts.day_of_week"}
                    category={"contract"}
                    required={true}
                    daysInt={true}
                    mode="multiple"
                  />
                </AnimationChildWrapper>
              )}

              <AnimationChildWrapper key={3} itemKey={3}>
                <HourPickerItem
                  name={"shifts.start_time_minutes"}
                  category={"contract"}
                  required={true}
                />
              </AnimationChildWrapper>
              <AnimationChildWrapper key={4} itemKey={4}>
                <InputItem
                  number={true}
                  name={"shifts.end_time_minutes"}
                  category="contract"
                  min={0.5}
                  max={24}
                  twoDigitsNumbers={true}
                  required={true}
                  step={0.5}
                />
              </AnimationChildWrapper>

              <AnimationChildWrapper key={5} itemKey={5}>
                <RestrictedSettingsDataSelect
                  name="shifts.client_tariff_id"
                  category="contract"
                  settingsdataName={"accounting/client_tariffs"}
                  paramName={"cost"}
                  required={true}
                />
              </AnimationChildWrapper>

              <AnimationChildWrapper key={6} itemKey={6}>
                <InputItem
                  number={true}
                  name={"shifts.hourly_percentage_rate"}
                  category="contract"
                  min={0}
                  max={500}
                  twoDigitsNumbers={true}
                  step={1}
                  required={true}
                />
              </AnimationChildWrapper>

              <AnimationChildWrapper key={7} itemKey={7}>
                <RestrictedSettingsDataSelect
                  name={"shifts.salary_symbol_id"}
                  category="contract"
                  settingsdataName={"system_tables/salary/salary_symbols"}
                  paramName={"symbol"}
                  required={true}
                />
              </AnimationChildWrapper>
              {overlappingShifts.length > 0 && (
                <AnimationChildWrapper key={8} itemKey={8}>
                  <div>
                    <Text style={{ color: "red" }}>
                      {
                        <FormattedMessage id="app.contracts.overlappingShiftsWarning" />
                      }
                    </Text>
                    <Popover
                      content={content}
                      title={
                        <FormattedMessage id="app.contracts.overlappingShifts" />
                      }
                    >
                      <InfoCircleOutlined style={{ padding: "0 5px" }} />
                    </Popover>
                  </div>
                </AnimationChildWrapper>
              )}
            </Col>

            <Row style={{ width: "100%" }}>
              <Col span={24}>
                <FormActions
                  resource={resource}
                  action={action}
                  onCancel={onCancel}
                  hideDelete={true}
                  hideDecline={true}
                  mode={mode}
                  onDecline={onCancel}
                  disabled={disabled}
                />
              </Col>
            </Row>
          </Row>
        </ChildrenAnimationWrapper>
      </Form>
    </div>
  );
};

Shift.propTypes = {
  mode: PropTypes.string,
  onFinish: PropTypes.func,
  onFinishFailed: PropTypes.func,
  onDecline: PropTypes.func,
  category: PropTypes.string,
  name: PropTypes.string,
  resource: PropTypes.string,
  action: PropTypes.string,
};

export default Shift;
